import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';
import { combinedDeviceDossierUrl, type DossierResponse } from '@/features/dossier/api';
import { useCombinedDeviceDossier } from '@/features/dossier/hooks';
import { EuDossierHeader } from './DossierResultRenderer';
import { CombinedIdentityCard } from './CombinedIdentityCard';
import { SectionBlock, type RenderedSection } from './CombinedDossierSection';
import { GsprMatrixRenderer } from './GsprMatrixRenderer';
import { mdrDossierProfile } from './euMdrDossierProfile';
import { ivdrDossierProfile } from './euIvdrDossierProfile';
import {
  PartPendingNote,
  pickDossier,
  pickSubmissionHeader,
  type DossierRenderCtx,
} from './euDossierProfile';

/**
 * The COMPLETE EU dossier rendered on the result page in the **US FDA
 * result-page DESIGN** — a header card + clean, primitive-built React sections.
 * NOT the PDF's layout (no cover, no print TOC, no dense print tables); the
 * per-part renderers already use the app's look.
 *
 * Separation of concern: this file is a regulation-agnostic SHELL. All the
 * regulation-specific decisions — which Parts exist, their order, titles,
 * article numbers, class applicability, and which renderer each uses — live in
 * two independent profiles (`euMdrDossierProfile` / `euIvdrDossierProfile`),
 * mirroring the backend's two combined renderers. The shell picks one profile by
 * class and never branches on regulation itself, so changing one regulation
 * cannot affect the other.
 *
 * PART NUMBERS MATCH THE DOWNLOADED PDF (the artifact forwarded to RA): the
 * profiles encode the same applicability the combined renderers use, so a Part
 * that isn't applicable is omitted, while an applicable-but-ungenerated Part is
 * still numbered and shown as "pending" — the on-screen numbering never drifts.
 *
 * Section order (driven by the active profile):
 *   1. Numbered Parts (document order); the shared GSPR Annex I checklist is
 *      interleaved right after the TD Part (not numbered, not in Contents).
 *   2. Appendices after the numbered Parts (present-only; PSUR, references, and
 *      — MDR only — implant card / SSCP lay summary).
 *
 * Data source: the assembled `CombinedEuParts` JSON from
 * GET /api/products/{id}/eu-dossier?format=json (MDR) or .../ivdr-dossier (IVDR).
 */

const IVDR_CLASSES = new Set(['A', 'A_sterile', 'B', 'C', 'D']);

export function CombinedEuDossierView({
  productId,
  deviceClass,
  country = 'EU',
  runId = null,
  productType = null,
}: {
  productId: number;
  deviceClass: string;
  /** "EU" (default) reads the EU runs; "NI" reads the Northern Ireland (Windsor)
   *  MDR runs via /ni-dossier. NI here is always MDR — NI IVDR uses the dedicated
   *  CombinedNiIvdrDossierView, so it never reaches this component. */
  country?: string;
  /** Any run of the dispatch this complete dossier belongs to: pins the page and its Download
   *  PDF to that dispatch (spec 2026-10-23 B). Null = the product's latest completed dispatch. */
  runId?: number | null;
  /** Product type for the provenance header (the grouped annexes don't carry it;
   *  threaded from the representative run's dossier_response). */
  productType?: string | null;
}) {
  const isIvdr = IVDR_CLASSES.has(deviceClass);
  const isNi = country.toUpperCase() === 'NI';
  const profile = isIvdr ? ivdrDossierProfile : mdrDossierProfile;
  const kind = isNi ? 'ni' : isIvdr ? 'ivdr' : 'eu';
  const url = (fmt: 'pdf') => combinedDeviceDossierUrl(kind, productId, deviceClass, fmt, runId);

  const { parts, failed } = useCombinedDeviceDossier(kind, productId, deviceClass, runId);

  if (failed) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <Button variant="brand" size="sm" asChild>
            <a
              href={url('pdf')}
              target="_blank"
              rel="noopener"
              title="Download the full EU dossier as PDF"
            >
              <Download className="size-3.5" /> Download PDF
            </a>
          </Button>
        </div>
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6 text-state-error-text text-sm">
          Couldn't load the complete dossier in the page. Use Download PDF, or refresh.
        </div>
      </div>
    );
  }

  if (parts === null) {
    return <RunLoadingSkeleton label="Loading the complete dossier" />;
  }

  // Build a minimal DossierResponse for the shared EuDossierHeader. The header
  // reads product_name, submission_type (the device class), submission_header
  // (manufacturer / EC REP / NB / signatory / contact — best-effort from an
  // annex part) and dossier.basic_udi_di.
  const annexHeader =
    pickSubmissionHeader(parts.td) ??
    pickSubmissionHeader(parts.pms) ??
    pickSubmissionHeader(parts.sscp);
  const annexDossier =
    pickDossier(parts.td) ?? pickDossier(parts.pms) ?? pickDossier(parts.sscp) ?? {};
  const headerResponse = {
    product_id: String(productId),
    product_name: parts.device_name ?? '',
    product_type: productType ?? '',
    submission_type: parts.device_class,
    model: '',
    iterations: 0,
    dossier: annexDossier,
    submission_header: annexHeader ?? undefined,
    tool_calls: [],
    warnings: [],
    dossier_run_id: 0,
  } as DossierResponse;

  const ctx: DossierRenderCtx = { productId, deviceClass };

  // ── NUMBERED PARTS ─────────────────────────────────────────────────────────
  // Keep only the active profile's Parts applicable to this class; number them
  // 1..N in document order. An applicable-but-ungenerated Part keeps its number
  // and renders as "pending" — this is what makes the page's Part numbers
  // identical to the combined PDF's.
  const numbered: RenderedSection[] = profile.numberedParts
    .filter((spec) => spec.applicable(deviceClass))
    .map((spec, i) => {
      const part = spec.pick(parts);
      return {
        id: spec.id,
        title: `Part ${i + 1} — ${spec.title}`,
        body: part == null ? <PartPendingNote /> : spec.render(part, ctx),
      };
    });

  // ── GSPR — shared appendix interleaved right after the TD Part ──────────────
  // In the combined PDF, GSPR renders immediately after the TD part's content
  // (`if attr == "td" and p.gspr_matrix: ...`). It is NOT a numbered Part and NOT
  // in the Contents list. The same renderer + placement serve both regulations,
  // so it lives in the shell, not the profiles.
  const gspr: RenderedSection | null = parts.gspr_matrix
    ? {
        id: 'eu-sec-gspr_matrix',
        title: 'GSPR Conformity Checklist (Annex I)',
        body: <GsprMatrixRenderer matrix={parts.gspr_matrix as Record<string, unknown>} />,
      }
    : null;

  // ── APPENDICES — after all numbered Parts (present-only, not numbered) ───────
  const appendixSections: RenderedSection[] = [];
  for (const spec of profile.appendices) {
    const part = spec.pick(parts);
    if (part != null) {
      appendixSections.push({ id: spec.id, title: spec.title, body: spec.render(part, ctx) });
    }
  }

  // ── Build the interleaved render list ───────────────────────────────────────
  const tdIdx = numbered.findIndex((s) => s.id === 'eu-sec-td');
  const renderOrder: RenderedSection[] = [];
  numbered.forEach((s, i) => {
    renderOrder.push(s);
    if (i === tdIdx && gspr) {
      renderOrder.push(gspr);
    }
  });
  // If TD was absent but GSPR is present, append GSPR after the numbered Parts.
  if (tdIdx === -1 && gspr) {
    renderOrder.push(gspr);
  }
  for (const a of appendixSections) {
    renderOrder.push(a);
  }

  // Single Download PDF action — sits on the right of the FIRST section's
  // heading row (Part 1 — Technical Documentation), mirroring the US FDA page
  // where Download PDF sits to the right of the dossier heading.
  const downloadAction = (
    <Button variant="brand" size="sm" asChild>
      <a
        href={url('pdf')}
        target="_blank"
        rel="noopener"
        title="Download the full EU dossier as PDF"
      >
        <Download className="size-3.5" /> Download PDF
      </a>
    </Button>
  );

  return (
    <div>
      <EuDossierHeader
        response={headerResponse}
        eyebrow={isNi ? 'UK (Northern Ireland) MDR Dossier' : 'EU Submission Dossier'}
      />
      {/* MDR (EU + NI): the identity the PDF cover states. IVDR has no identity block yet. */}
      {!isIvdr && <CombinedIdentityCard identity={parts.identity} country={country} />}

      {/* Sections — full-width, single column. */}
      <div className="mt-6 space-y-8">
        {renderOrder.map((s, i) => (
          <SectionBlock key={s.id} view="CombinedEuDossierView" section={s} action={i === 0 ? downloadAction : undefined} />
        ))}
      </div>
    </div>
  );
}
